import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SearchIcon } from '@/components/icons'
import { cn } from '@/utils/cn'

/**
 * 顶栏搜索框（设计稿 D1/D2 顶部长条、D3 顶部、M3 顶部）。
 *
 * 提交走 `<form>` 而不是监听 Enter 键：移动端软键盘会把「回车」渲染成
 * 「搜索」按钮，那是浏览器根据 form 推断出来的，手写 keydown 拿不到。
 *
 * 输入值受控于 `value` prop（搜索页需要用 URL 里的 q 回填），但**不做即时搜索**：
 * 后端的 FTS 对无空格中文召回有限，边打边搜会在中途给出一堆无意义的结果。
 * 显式提交更符合这个后端的能力。
 */

export interface SearchInputProps {
  /** 受控初值。搜索页传 URL 中的 q，首页传空。 */
  value?: string
  placeholder?: string
  className?: string
  /** 自定义提交行为；缺省跳转到 /search?q=…。 */
  onSubmit?: (query: string) => void
  autoFocus?: boolean
}

export function SearchInput({
  value = '',
  placeholder = '搜索游戏、移植包、社团',
  className,
  onSubmit,
  autoFocus = false,
}: SearchInputProps) {
  const [draft, setDraft] = useState(value)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  // 外部值变化时同步（例如从搜索页点了某个标签，URL 的 q 被清空）。
  useEffect(() => setDraft(value), [value])

  return (
    <form
      role="search"
      className={cn('relative flex-1', className)}
      onSubmit={(event) => {
        event.preventDefault()
        const query = draft.trim()
        if (onSubmit) {
          onSubmit(query)
          return
        }
        // 空查询也允许提交：它等于「浏览全部」，落到 /search 的默认排序。
        navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search')
        // 移动端提交后收起键盘，否则结果被键盘挡住。
        inputRef.current?.blur()
      }}
    >
      <label htmlFor="site-search" className="sr-only">
        搜索站内内容
      </label>
      <SearchIcon
        className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        id="site-search"
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        // enterKeyHint 让移动端软键盘把回车键显示成「搜索」。
        enterKeyHint="search"
        autoComplete="off"
        className="h-10 w-full rounded-card border border-border bg-surface pl-10 pr-3 text-sm text-foreground transition-colors placeholder:text-subtle-foreground hover:border-border-strong focus:border-primary sm:h-11"
      />
    </form>
  )
}
